import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = [
    "progressBar",
    "statusMessage",
    "artifactList",
    "generationButton",
    "syncButton",
    "buildButton",
    "activeUsers",
    "notifications"
  ]

  static values = {
    projectId: String,
    userId: String,
    wsUrl: String
  }

  connect() {
    this.setupWebSocket()
    this.setupEventListeners()
    this.initializeProgress()
    this.startPresenceTracking()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    this.stopPresenceTracking()
  }

  setupWebSocket() {
    const consumer = createConsumer(this.wsUrlValue || "/cable")
    
    this.subscription = consumer.subscriptions.create(
      {
        channel: "SprintZeroChannel",
        app_project_id: this.projectIdValue
      },
      {
        connected: () => {
          this.handleConnected()
        },
        disconnected: () => {
          this.handleDisconnected()
        },
        received: (data) => {
          this.handleWebSocketMessage(data)
        }
      }
    )
  }

  setupEventListeners() {
    // Listen for custom events from other controllers
    this.element.addEventListener("sprint-zero:generate", this.handleGenerateEvent.bind(this))
    this.element.addEventListener("sprint-zero:sync", this.handleSyncEvent.bind(this))
    this.element.addEventListener("sprint-zero:build", this.handleBuildEvent.bind(this))
    
    // Listen for visibility change to pause/resume updates
    document.addEventListener("visibilitychange", this.handleVisibilityChange.bind(this))
  }

  initializeProgress() {
    // Load initial progress from data attributes
    const progress = parseInt(this.element.dataset.progress || 0)
    this.updateProgressBar(progress)
  }

  startPresenceTracking() {
    // Send presence ping every 30 seconds
    this.presenceInterval = setInterval(() => {
      if (this.subscription) {
        this.subscription.perform("presence_ping")
      }
    }, 30000)
  }

  stopPresenceTracking() {
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval)
    }
  }

  // WebSocket message handlers
  handleWebSocketMessage(data) {
    switch(data.type) {
      case 'user_joined':
        this.handleUserJoined(data)
        break
      case 'user_left':
        this.handleUserLeft(data)
        break
      case 'artifact_updated':
        this.handleArtifactUpdated(data)
        break
      case 'generation_started':
        this.handleGenerationStarted(data)
        break
      case 'generation_complete':
        this.handleGenerationComplete(data)
        break
      case 'linear_sync_started':
        this.handleSyncStarted(data)
        break
      case 'linear_sync_complete':
        this.handleSyncComplete(data)
        break
      case 'build_progress':
        this.handleBuildProgress(data)
        break
      case 'build_completed':
        this.handleBuildComplete(data)
        break
      case 'cursor_move':
        this.handleCursorMove(data)
        break
      case 'content_change':
        this.handleContentChange(data)
        break
      default:
        console.log('Unknown WebSocket message type:', data.type)
    }
  }

  handleConnected() {
    this.showStatusMessage("Connected to Sprint Zero", "success")
    this.element.classList.add("connected")
  }

  handleDisconnected() {
    this.showStatusMessage("Disconnected - Reconnecting...", "warning")
    this.element.classList.remove("connected")
  }

  // User presence handlers
  handleUserJoined(data) {
    this.updateActiveUsers(data.active_users)
    this.showNotification(`${data.user.name} joined`, "info")
  }

  handleUserLeft(data) {
    this.updateActiveUsers(data.active_users)
    this.showNotification(`${data.user.name} left`, "info")
  }

  updateActiveUsers(users) {
    if (!this.hasActiveUsersTarget) return
    
    const userList = users.map(user => `
      <div class="flex items-center space-x-2 px-2 py-1">
        <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span class="text-sm">${user.user_name}</span>
      </div>
    `).join('')
    
    this.activeUsersTarget.innerHTML = userList
  }

  // Artifact update handlers
  handleArtifactUpdated(data) {
    const artifactElement = document.querySelector(`[data-artifact-id="${data.artifact_id}"]`)
    if (artifactElement) {
      artifactElement.classList.add("animate-pulse")
      setTimeout(() => {
        artifactElement.classList.remove("animate-pulse")
      }, 2000)
    }
    
    this.showNotification(`${data.artifact_type} updated by ${data.updated_by.name}`, "info")
    this.refreshArtifactList()
  }

  // Generation handlers
  handleGenerationStarted(data) {
    this.showStatusMessage(`Generating ${data.generation_type}...`, "info")
    this.disableGenerationButtons()
    this.startProgressAnimation()
  }

  handleGenerationComplete(data) {
    this.showStatusMessage(`${data.generation_type} generation complete!`, "success")
    this.enableGenerationButtons()
    this.stopProgressAnimation()
    this.refreshArtifactList()
    this.updateProgressBar(data.progress || 0)
  }

  // Sync handlers
  handleSyncStarted(data) {
    this.showStatusMessage(`Linear sync started (${data.sync_type})...`, "info")
    if (this.hasSyncButtonTarget) {
      this.syncButtonTarget.disabled = true
      this.syncButtonTarget.classList.add("opacity-50")
    }
  }

  handleSyncComplete(data) {
    const message = `Linear sync complete: ${data.created_issues || 0} created, ${data.updated_issues || 0} updated`
    this.showStatusMessage(message, "success")
    if (this.hasSyncButtonTarget) {
      this.syncButtonTarget.disabled = false
      this.syncButtonTarget.classList.remove("opacity-50")
    }
  }

  // Build handlers
  handleBuildProgress(data) {
    this.updateProgressBar(data.progress)
    this.showStatusMessage(data.message, "info")
  }

  handleBuildComplete(data) {
    const status = data.success ? "success" : "error"
    const message = data.success 
      ? `Build completed successfully (${data.artifact_count} artifacts)`
      : "Build failed - check logs for details"
    
    this.showStatusMessage(message, status)
    this.updateProgressBar(100)
    
    if (this.hasBuildButtonTarget) {
      this.buildButtonTarget.disabled = false
      this.buildButtonTarget.classList.remove("opacity-50")
    }
  }

  // Collaboration handlers
  handleCursorMove(data) {
    // Update cursor position for collaborative editing
    this.updateUserCursor(data.user, data.position, data.element_id)
  }

  handleContentChange(data) {
    // Update content in real-time for collaboration
    const element = document.querySelector(`#${data.element_id}`)
    if (element && element.dataset.userId !== this.userIdValue) {
      element.value = data.content
      this.showCollaborationIndicator(element, data.user)
    }
  }

  updateUserCursor(user, position, elementId) {
    let cursor = document.querySelector(`[data-cursor-user-id="${user.id}"]`)
    
    if (!cursor) {
      cursor = document.createElement("div")
      cursor.className = "absolute w-0.5 h-5 animate-pulse pointer-events-none z-50"
      cursor.style.backgroundColor = user.color || "#3B82F6"
      cursor.dataset.cursorUserId = user.id
      
      const label = document.createElement("div")
      label.className = "absolute -top-5 left-0 text-xs px-1 rounded text-white whitespace-nowrap"
      label.style.backgroundColor = user.color || "#3B82F6"
      label.textContent = user.name
      cursor.appendChild(label)
      
      document.body.appendChild(cursor)
    }
    
    const element = document.getElementById(elementId)
    if (element) {
      const rect = element.getBoundingClientRect()
      cursor.style.left = `${rect.left + position.x}px`
      cursor.style.top = `${rect.top + position.y}px`
    }
  }

  showCollaborationIndicator(element, user) {
    element.style.borderColor = user.color || "#3B82F6"
    element.classList.add("ring-2")
    
    setTimeout(() => {
      element.style.borderColor = ""
      element.classList.remove("ring-2")
    }, 2000)
  }

  // Action handlers
  generateArtifact(event) {
    const button = event.currentTarget
    const artifactType = button.dataset.artifactType
    
    if (!artifactType) return
    
    button.disabled = true
    button.classList.add("opacity-50")
    
    this.subscription.perform("start_generation", {
      generation_type: artifactType,
      options: {}
    })
  }

  syncWithLinear(event) {
    const button = event.currentTarget
    button.disabled = true
    button.classList.add("opacity-50")
    
    this.subscription.perform("sync_linear", {
      sync_type: "incremental"
    })
  }

  startBuild(event) {
    const button = event.currentTarget
    button.disabled = true
    button.classList.add("opacity-50")
    
    // Get build configuration from form or defaults
    const config = this.getBuildConfiguration()
    
    fetch(`/sprint_zero/${this.projectIdValue}/build/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ build_config: config })
    })
    .then(response => response.json())
    .then(data => {
      if (data.build_session_id) {
        this.showStatusMessage("Build started successfully", "success")
        this.subscribeToBuildProgress(data.build_session_id)
      }
    })
    .catch(error => {
      this.showStatusMessage("Failed to start build", "error")
      button.disabled = false
      button.classList.remove("opacity-50")
    })
  }

  subscribeToBuildProgress(buildSessionId) {
    // Subscribe to build progress channel
    const consumer = createConsumer()
    this.buildSubscription = consumer.subscriptions.create(
      {
        channel: "BuildProgressChannel",
        build_session_id: buildSessionId
      },
      {
        received: (data) => {
          this.handleBuildProgress(data)
        }
      }
    )
  }

  getBuildConfiguration() {
    // Get configuration from form elements if they exist
    const form = document.querySelector('[data-build-config-form]')
    if (form) {
      return Object.fromEntries(new FormData(form))
    }
    
    // Return default configuration
    return {
      target_directory: "/tmp/sprint_zero_build",
      include_tests: true,
      generate_readme: true,
      include_docker: false
    }
  }

  // UI update methods
  updateProgressBar(progress) {
    if (!this.hasProgressBarTarget) return
    
    this.progressBarTarget.style.width = `${progress}%`
    this.progressBarTarget.setAttribute('aria-valuenow', progress)
    
    // Update text if present
    const progressText = this.progressBarTarget.querySelector('.progress-text')
    if (progressText) {
      progressText.textContent = `${progress}%`
    }
    
    // Update color based on progress
    if (progress < 30) {
      this.progressBarTarget.className = "progress-bar bg-red-500"
    } else if (progress < 70) {
      this.progressBarTarget.className = "progress-bar bg-yellow-500"
    } else {
      this.progressBarTarget.className = "progress-bar bg-green-500"
    }
  }

  showStatusMessage(message, type = "info") {
    if (!this.hasStatusMessageTarget) return
    
    this.statusMessageTarget.textContent = message
    this.statusMessageTarget.className = `alert alert-${type}`
    
    // Auto-hide after 5 seconds for non-error messages
    if (type !== "error") {
      setTimeout(() => {
        this.statusMessageTarget.textContent = ""
        this.statusMessageTarget.className = "alert"
      }, 5000)
    }
  }

  showNotification(message, type = "info") {
    if (!this.hasNotificationsTarget) return
    
    const notification = document.createElement("div")
    notification.className = `notification notification-${type} animate-slide-in`
    notification.innerHTML = `
      <div class="flex items-center justify-between">
        <span>${message}</span>
        <button class="ml-4" data-action="click->sprint-zero#dismissNotification">×</button>
      </div>
    `
    
    this.notificationsTarget.appendChild(notification)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.classList.add("animate-slide-out")
      setTimeout(() => notification.remove(), 300)
    }, 5000)
  }

  dismissNotification(event) {
    const notification = event.target.closest('.notification')
    if (notification) {
      notification.classList.add("animate-slide-out")
      setTimeout(() => notification.remove(), 300)
    }
  }

  refreshArtifactList() {
    // Reload the artifact list via Turbo
    if (this.hasArtifactListTarget) {
      const url = this.artifactListTarget.dataset.refreshUrl
      if (url) {
        fetch(url, {
          headers: {
            'Accept': 'text/vnd.turbo-stream.html'
          }
        })
        .then(response => response.text())
        .then(html => {
          Turbo.renderStreamMessage(html)
        })
      }
    }
  }

  startProgressAnimation() {
    if (!this.hasProgressBarTarget) return
    
    this.progressBarTarget.classList.add("animate-pulse")
  }

  stopProgressAnimation() {
    if (!this.hasProgressBarTarget) return
    
    this.progressBarTarget.classList.remove("animate-pulse")
  }

  disableGenerationButtons() {
    if (this.hasGenerationButtonTarget) {
      this.generationButtonTargets.forEach(button => {
        button.disabled = true
        button.classList.add("opacity-50")
      })
    }
  }

  enableGenerationButtons() {
    if (this.hasGenerationButtonTarget) {
      this.generationButtonTargets.forEach(button => {
        button.disabled = false
        button.classList.remove("opacity-50")
      })
    }
  }

  // Event handlers
  handleGenerateEvent(event) {
    this.generateArtifact(event)
  }

  handleSyncEvent(event) {
    this.syncWithLinear(event)
  }

  handleBuildEvent(event) {
    this.startBuild(event)
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopPresenceTracking()
    } else {
      this.startPresenceTracking()
      // Send immediate presence ping when becoming visible
      if (this.subscription) {
        this.subscription.perform("presence_ping")
      }
    }
  }
}